import { useEffect, useState } from 'react';
import { Modal, Button } from '@symphenia/ui-components';
import { fieldLabel, inputBase } from '@/lib/form-styles';
import { fetchMonthChanges, startMonthChange, type MonatsAenderung } from '../api/payroll.api';

/** Die Felder einer Tageszeile, wie sie im Raster heißen. */
const FELD: Record<string, string> = {
  startTime: 'Kommt',
  endTime: 'Geht',
  breakMinutes: 'Pause',
  grossOverride: 'Gesamtstunden',
  netOverride: 'Netto-Arbeitszeit',
  travelHours: 'Reise',
  departure: 'Abfahrt',
  returnTime: 'Rückfahrt',
  km: 'km',
  isDriver: 'Lenker',
  overnight: 'Nächtigung',
  absenceTypeId: 'Abwesenheit',
  absenceScope: 'Umfang',
  absences: 'Fehlzeiten',
  appointment: 'Termin',
  appointmentHours: 'Termindauer',
  note: 'Bemerkung',
  slip: 'Abgabezettel',
};

function zpad(n: number): string {
  return String(n).padStart(2, '0');
}

function datumZeit(iso: string): string {
  const d = new Date(iso);
  return `${zpad(d.getDate())}.${zpad(d.getMonth() + 1)}.${d.getFullYear()}, ${zpad(d.getHours())}:${zpad(d.getMinutes())}`;
}

function tagText(iso: string): string {
  return `${iso.slice(8, 10)}.${iso.slice(5, 7)}.`;
}

interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  jahr: number;
  monat: number;
  bezeichnung: string;
  onBegonnen: (vorgang: { id: string; reason: string | null }) => void;
}

/**
 * „Monat ändern" im Modus **mit Grund** (AP-22 Teil 2, G2) — eine Handlung
 * (AP-36 Fall 3). Der Grund gilt für den ganzen Vorgang, nicht je Feld: Danach
 * wird im Raster wie gewohnt feldweise gespeichert.
 */
export function AendernDialog({ isOpen, onClose, userId, jahr, monat, bezeichnung, onBegonnen }: DialogProps) {
  const [grund, setGrund] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setGrund('');
    setError(null);
  }, [isOpen]);

  const beginnen = async () => {
    if (grund.trim().length < 3) return setError('Bitte einen Grund angeben.');
    setLaeuft(true);
    setError(null);
    try {
      onBegonnen(await startMonthChange(userId, jahr, monat, grund.trim()));
    } catch {
      setError('Die Änderung konnte nicht begonnen werden.');
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Monat ändern — ${bezeichnung}`}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={laeuft}>
            Abbrechen
          </Button>
          <Button onClick={() => void beginnen()} disabled={laeuft}>
            Monat ändern
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Der Monat ist fertiggestellt. Jede Änderung wird mit diesem Grund, deinem Namen und der Uhrzeit
          festgehalten — samt altem und neuem Wert.
        </p>
        <div className="space-y-1.5">
          <label className={fieldLabel} htmlFor="aenderung-grund">
            Grund
          </label>
          <textarea
            id="aenderung-grund"
            className={`${inputBase} min-h-[5rem]`}
            placeholder="z. B. Stunden am 04.09. nachgetragen"
            maxLength={500}
            value={grund}
            onChange={(e) => setGrund(e.target.value)}
            autoFocus
          />
        </div>
        {error && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}
      </div>
    </Modal>
  );
}

function wertText(feld: string, wert: unknown, arten: Map<string, string>): string {
  if (wert === null || wert === undefined || wert === '') return 'leer';
  if (wert === true) return 'ja';
  if (feld === 'absenceTypeId') return arten.get(String(wert)) ?? '—';
  if (feld === 'absenceScope') return wert === 'HALF' ? 'halb' : 'ganz';
  // Teil 4: Die Liste steht als Text im Protokoll — lesbar gemacht: „U 3 h, K 5 h".
  if (feld === 'absences' && typeof wert === 'string') {
    try {
      return (JSON.parse(wert) as { absenceTypeId: string; hours: number }[])
        .map((a) => `${arten.get(a.absenceTypeId) ?? '?'} ${String(a.hours).replace('.', ',')} h`)
        .join(', ');
    } catch {
      return wert;
    }
  }
  if (typeof wert === 'number') return String(wert).replace('.', ',');
  return String(wert);
}

/**
 * Was nach dem Fertigstellen geändert wurde: je Vorgang wer, wann und warum,
 * darunter je Feld der Tag, alt und neu. Lädt neu, sobald sich `stand` ändert.
 */
export function Aenderungsprotokoll({
  userId,
  jahr,
  monat,
  stand,
  arten,
}: {
  userId: string;
  jahr: number;
  monat: number;
  /** Zeitpunkt der letzten Änderung am Lohnmonat — wechselt er, ist das Protokoll neu. */
  stand: string | null;
  arten: { id: string; code: string; name: string }[];
}) {
  const [liste, setListe] = useState<MonatsAenderung[] | null>(null);

  useEffect(() => {
    if (!stand) {
      setListe(null);
      return;
    }
    let aktuell = true;
    fetchMonthChanges(userId, jahr, monat)
      .then((l) => aktuell && setListe(l))
      .catch(() => aktuell && setListe(null));
    return () => {
      aktuell = false;
    };
  }, [userId, jahr, monat, stand]);

  if (!liste || liste.length === 0) return null;
  const artName = new Map(arten.map((a) => [a.id, `${a.code} · ${a.name}`]));

  return (
    <div className="space-y-2 rounded-lg border border-border bg-card p-4 shadow-sm">
      <h2 className="text-sm font-semibold text-foreground">Änderungen nach dem Fertigstellen</h2>
      <ul className="space-y-3">
        {liste.map((v) => (
          <li key={v.id} className="text-sm">
            <p className="text-foreground">
              <span className="tabular-nums">{datumZeit(v.startedAt)}</span> · {v.by}
              {v.reason ? <span className="text-muted-foreground"> — {v.reason}</span> : null}
            </p>
            <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
              {v.entries.map((e, i) => (
                <li key={i} className="tabular-nums">
                  {e.date ? `${tagText(e.date)} ` : ''}
                  {FELD[e.field] ?? e.field}: {wertText(e.field, e.from, artName)} →{' '}
                  <span className="text-foreground">{wertText(e.field, e.to, artName)}</span>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
}
